/**
 * Tiny typed fetch wrapper around the TAKSIKORTTI API.
 *
 * Auth: we acquire an AAD access token from the shared MSAL instance and
 * send it as `Authorization: Bearer <token>`. The API validates it. If
 * silent acquisition fails (expired/again interaction-required), we kick
 * off an interactive redirect.
 *
 * Error shape mirrors api/src/functions/lib/http.ts. We surface it as
 * a structured `ApiError` so React Query mutation handlers can branch
 * on `code` (e.g. show the "case was updated, reload" toast on 412
 * stale_version).
 */

import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { msalInstance } from '../auth/AuthProvider';
import { loginRequest } from '../auth/msalConfig';

/**
 * Acquire a bearer token header from the MSAL cache. Falls back to an
 * interactive redirect when the refresh token can't be used silently.
 * Exported for the rare non-JSON call (binary download in api/korttitoimitus.ts).
 */
export async function authHeader(): Promise<string | null> {
  const account = msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0];
  if (!account) return null;
  try {
    const res = await msalInstance.acquireTokenSilent({ ...loginRequest, account });
    return `Bearer ${res.accessToken}`;
  } catch (e) {
    if (e instanceof InteractionRequiredAuthError) {
      await msalInstance.acquireTokenRedirect(loginRequest);
    }
    return null;
  }
}

export interface ApiErrorBody {
  code: string;
  message?: string;
  [key: string]: unknown;
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: ApiErrorBody,
  ) {
    super(body.message ?? `HTTP ${status}`);
  }
}

export interface FetchOptions extends Omit<RequestInit, 'body' | 'method'> {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE' | 'PUT';
  body?: unknown;
  /** When true, body is sent as-is (Buffer / ArrayBuffer / string). For CSV upload. */
  rawBody?: boolean;
}

const API_BASE = '/api';

export async function api<T>(path: string, opts: FetchOptions = {}): Promise<T> {
  const method = opts.method ?? 'GET';
  const headers = new Headers(opts.headers);

  const auth = await authHeader();
  if (auth) headers.set('authorization', auth);

  const init: RequestInit = {
    method,
    headers,
  };

  if (opts.body !== undefined) {
    if (opts.rawBody) {
      init.body = opts.body as BodyInit;
    } else {
      headers.set('content-type', 'application/json');
      init.body = JSON.stringify(opts.body);
    }
  }

  const res = await fetch(`${API_BASE}${path}`, init);

  if (!res.ok) {
    let payload: ApiErrorBody = { code: `http_${res.status}` };
    try {
      payload = (await res.json()) as ApiErrorBody;
    } catch {
      // body wasn't JSON — keep the default shape
    }
    throw new ApiError(res.status, payload);
  }

  if (res.status === 204) return undefined as T;
  const text = await res.text();
  if (!text) return undefined as T;
  return JSON.parse(text) as T;
}
